import React, { useState } from 'react';
import { DB } from '../data/database';
import { ROLES, CUT_TYPES, CURRENCIES, exportCSV } from '../data/constants';
import { formatPrice } from '../utils/currency';
import { COUNTRY_LANG, LANGUAGES } from '../data/translations';
import { useLanguage } from '../translations/LanguageContext';
import { logActivity } from '../data/notifications';
import { generateCustomerStockPDF } from '../utils/generateInvoicePDF';
import StatCard from './ui/StatCard';
import DetailModal from './modals/DetailModal';
import {
  PlusIcon, PackageIcon, CarIcon, EngineIcon, PartsIcon,
  StockIcon, ActivityIcon, MenuIcon, RequestIcon, EditIcon
} from './Icons';

// ── small inline grid icon (no need to add to Icons.jsx) ──
const GridIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="7" height="7" rx="1"/>
    <rect x="14" y="3" width="7" height="7" rx="1"/>
    <rect x="3" y="14" width="7" height="7" rx="1"/>
    <rect x="14" y="14" width="7" height="7" rx="1"/>
  </svg>
);

const BAR_COLORS  = ['var(--blue2)', 'var(--green2)', 'var(--purple)', 'var(--orange)', 'var(--yellow)'];
const RANK_LABELS = ['🥇', '🥈', '🥉'];
const TYPE_MAP    = { vehicle: 'vehicles', engine: 'engines', part: 'parts' };

// ── Add Customer Modal ────────────────────────────────────────
function AddCustomerModal({ onSave, onClose }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: '', company: '', email: '', password: '',
    country: '', currency: 'USD', phone: '',
  });
  const [error, setError] = useState('');

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim())     return setError('Name is required.');
    if (!form.email.trim())    return setError('Email / username is required.');
    if (!form.password.trim()) return setError('Password is required.');
    if (!form.country.trim())  return setError('Country is required.');

    const existing = DB.get('customers') || [];
    if (existing.some(c => c.email === form.email.trim())) {
      return setError('A customer with this email already exists.');
    }

    const newCustomer = {
      id:        DB.genId('C'),
      name:      form.name.trim(),
      company:   form.company.trim(),
      email:     form.email.trim(),
      password:  form.password.trim(),
      country:   form.country.trim(),
      currency:  form.currency,
      phone:     form.phone.trim(),
      createdAt: new Date().toISOString().slice(0, 10),
    };

    DB.set('customers', [...existing, newCustomer]);
    logActivity.customerAdded(form.name.trim(), 'Admin');

    // Auto-add the customer's country language to the app language list
    const langCode = COUNTRY_LANG[form.country.trim().toLowerCase()];
    if (langCode && LANGUAGES[langCode]) {
      window.dispatchEvent(new CustomEvent('langAdded', { detail: langCode }));
    }

    onSave();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h3 className="modal-title">Add New Customer</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && (
            <div style={{ background: 'rgba(248,81,73,0.12)', border: '1px solid rgba(248,81,73,0.3)', borderRadius: 8, padding: '10px 14px', color: 'var(--red)', fontSize: 13, marginBottom: 14 }}>
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Full Name *</label>
                <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ahmed Al-Rashid" />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Company</label>
                <input className="form-input" value={form.company} onChange={e => set('company', e.target.value)} placeholder="Trading LLC" />
              </div>
              <div className="form-group">
                <label className="form-label">Email / Username *</label>
                <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="ahmed@trading.ae" />
              </div>
              <div className="form-group">
                <label className="form-label">Password *</label>
                <input className="form-input" type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="Set login password" />
              </div>
              <div className="form-group">
                <label className="form-label">Country *</label>
                <input className="form-input" value={form.country} onChange={e => set('country', e.target.value)} placeholder="UAE" />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+971501234567" />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Preferred Currency</label>
                <select className="form-input" value={form.currency} onChange={e => set('currency', e.target.value)}>
                  {Object.entries(CURRENCIES).map(([code, { symbol }]) => (
                    <option key={code} value={code}>{code} ({symbol})</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2 }}>Add Customer</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── PDF export (opens print dialog in new tab) ───────────────
function exportPDF(items, customerName) {
  if (!items.length) { alert('No data to export.'); return; }
  const getSubtype = (i) => {
    if (i.make) return i.cutType ? (CUT_TYPES.find(c => c.id === i.cutType)?.name || i.cutType) : (i.category || 'Full Car');
    if (i.engineName) return 'Engine';
    return i.category || 'Part';
  };
  const rows = items.map(i => `
    <tr>
      <td>${i.sku || ''}</td>
      <td>${i.chassis || ''}</td>
      <td>${i.make ? `${i.make} ${i.model}` : i.engineName || i.partName || ''}</td>
      <td>${i.make ? 'Vehicle' : i.engineName ? 'Engine' : 'Part'}</td>
      <td>${getSubtype(i)}</td>
      <td style="text-transform:capitalize">${(i.status || '').replace(/_/g, ' ')}</td>
      <td>${i.year || ''}</td>
      <td>${i.city || i.port || ''}</td>
      <td>${i.condition || ''}</td>
      <td>${i.daysInStock ?? ''}</td>
    </tr>`).join('');

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>${customerName} — Stock</title>
    <style>
      body{font-family:Arial,sans-serif;padding:28px;color:#111;font-size:12px}
      h1{font-size:20px;margin:0 0 4px}
      .sub{color:#666;font-size:11px;margin-bottom:20px}
      table{width:100%;border-collapse:collapse}
      th{background:#1a3a6b;color:#fff;text-align:left;padding:7px 10px;font-size:11px;font-weight:600}
      td{padding:6px 10px;border-bottom:1px solid #e0e0e0;vertical-align:top}
      tr:nth-child(even) td{background:#f6f8fb}
      @media print{body{padding:0}}
    </style></head><body>
    <h1>BIG STAR — ${customerName}</h1>
    <div class="sub">Stock Report · Generated ${new Date().toLocaleDateString('en-GB', {day:'2-digit',month:'short',year:'numeric'})}</div>
    <table>
      <tr><th>SKU</th><th>Chassis</th><th>Name</th><th>Type</th><th>Subtype</th><th>Status</th><th>Year</th><th>Location</th><th>Condition</th><th>Days</th></tr>
      ${rows}
    </table>
    <script>window.onload=()=>{window.print()}<\/script>
  </body></html>`;

  let iframe = document.getElementById('print-frame');
  if (!iframe) {
    iframe = document.createElement('iframe');
    iframe.id = 'print-frame';
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;';
    document.body.appendChild(iframe);
  }
  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open(); doc.write(html); doc.close();
  iframe.onload = () => { try { iframe.contentWindow.print(); } catch {} };
}

function Customers({ user, currency, lang }) {
  const { t } = useLanguage();
  const [selected,    setSelected]   = useState(() => {
    const deep = localStorage.getItem('bigstar_selected_customer');
    if (deep) { localStorage.removeItem('bigstar_selected_customer'); return deep; }
    return null;
  });
  const [profileTab,  setProfileTab] = useState('stock');
  const [search,      setSearch]     = useState('');
  const [detailUnit,  setDetailUnit] = useState(null);
  const [addModal,    setAddModal]   = useState(false);
  const [listSearch,  setListSearch] = useState('');
  const [dlOpen,      setDlOpen]     = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [custTab,     setCustTab]    = useState('all');
  const [, forceUpdate] = useState(0);

  const role      = ROLES[user?.role] || {};
  const canPrice  = role.canPrice  || false;
  const isCustomer = role.isCustomer || false;

  const customers = DB.get('customers')  || [];
  const vehicles  = DB.get('vehicles')   || [];
  const engines   = DB.get('engines')    || [];
  const parts     = DB.get('spareParts') || [];

  const getCustomerStats = (customerId) => {
    // STOCK — customer_stock entries (wishlist)
    const stockEntries = (DB.get('customerStock') || []).filter(s => s.customerId === customerId);
    const stockIds = new Set(stockEntries.map(s => s.unitId));
    const stockItems = [
      ...vehicles.filter(v => stockIds.has(v.id)).map(v => ({ ...v, _type: 'vehicle' })),
      ...engines.filter(e => stockIds.has(e.id)).map(e => ({ ...e, _type: 'engine' })),
      ...parts.filter(p => stockIds.has(p.id)).map(p => ({ ...p, _type: 'part' })),
    ];

    // SELECTED — units in pending selection orders
    const orders = (DB.get('selectionOrders') || []).filter(o => o.customerId === customerId);
    const pendingOrders = orders.filter(o => ['submitted', 'under_review'].includes(o.status));
    const selectedUnitIds = new Set(pendingOrders.flatMap(o => (o.units || []).filter(u => u.status === 'pending').map(u => u.unit_id)));
    const selectedItems = [
      ...vehicles.filter(v => selectedUnitIds.has(v.id)).map(v => ({ ...v, _type: 'vehicle' })),
      ...engines.filter(e => selectedUnitIds.has(e.id)).map(e => ({ ...e, _type: 'engine' })),
      ...parts.filter(p => selectedUnitIds.has(p.id)).map(p => ({ ...p, _type: 'part' })),
    ];

    // ASSIGNED — units where assignedTo = this customer (sold/approved)
    const assignedItems = [
      ...vehicles.filter(v => v.assignedTo === customerId).map(v => ({ ...v, _type: 'vehicle' })),
      ...engines.filter(e => e.assignedTo === customerId).map(e => ({ ...e, _type: 'engine' })),
      ...parts.filter(p => p.assignedTo === customerId).map(p => ({ ...p, _type: 'part' })),
    ];

    // Combined for backward compat
    const allItems = stockItems;
    const cv = stockItems.filter(i => i._type === 'vehicle');
    const ce = stockItems.filter(i => i._type === 'engine');
    const cp = stockItems.filter(i => i._type === 'part');
    const total = allItems.reduce((s, i) => s + (i.priceJPY || 0), 0);
    const topUnit = [...allItems].sort((a, b) => (b.priceJPY || 0) - (a.priceJPY || 0))[0] || null;

    // Outstanding balance
    const custInvoices = (DB.get('invoices') || []).filter(i => i.customerId === customerId && i.status !== 'cancelled' && i.status !== 'paid');
    const outstanding = custInvoices.reduce((s, i) => s + ((i.total || 0) - (i.amountPaid || 0)), 0);

    return {
      vehicles: cv, engines: ce, parts: cp, allItems, total, count: allItems.length, topUnit,
      stockItems, selectedItems, selectedOrders: pendingOrders, assignedItems, outstanding,
    };
  };

  // Save a unit update from DetailModal back to DB
  const handleUnitUpdate = (updatedItem, itemType) => {
    const key  = itemType === 'vehicles' ? 'vehicles' : itemType === 'engines' ? 'engines' : 'spareParts';
    const list = DB.get(key) || [];
    DB.set(key, list.map(i => i.id === updatedItem.id ? updatedItem : i));
    setDetailUnit(prev => ({ ...prev, item: updatedItem }));
    forceUpdate(v => v + 1);
  };

  // ── Block / unblock ─────────────────────────────────────────────────────
  const handleBlock = (customerId, currentlyBlocked) => {
    const msg = currentlyBlocked
      ? 'Unblock this customer and restore their access?'
      : 'Block this customer? They will not be able to log in until unblocked.';
    if (!window.confirm(msg)) return;
    const list = DB.get('customers') || [];
    DB.set('customers', list.map(c => c.id === customerId ? { ...c, blocked: !currentlyBlocked } : c));
    forceUpdate(v => v + 1);
  };

  // ── Delete customer ──────────────────────────────────────────────────────
  const handleDelete = (customerId) => {
    if (!window.confirm('Permanently delete this customer? Their assigned units will be unassigned. This cannot be undone.')) return;
    // Unassign units
    ['vehicles', 'engines', 'spareParts'].forEach(key => {
      const list = DB.get(key) || [];
      DB.set(key, list.map(u => u.assignedTo === customerId ? { ...u, assignedTo: '', status: 'available' } : u));
    });
    // Find the customer's Supabase ID before removing
    const customer = (DB.get('customers') || []).find(c => c.id === customerId);
    // Remove from localStorage
    DB.set('customers', (DB.get('customers') || []).filter(c => c.id !== customerId));
    // Delete from Supabase (both local id and _supaId)
    DB.deleteRecord('customers', customerId);
    if (customer?._supaId) DB.deleteRecord('customers', customer._supaId);
    setSelected(null);
    forceUpdate(v => v + 1);
  };

  // ── Derived analytics ────────────────────────────────────────────────────
  const allStats = customers
    .map(c => ({ ...c, stats: getCustomerStats(c.id) }))
    .sort((a, b) => b.stats.total - a.stats.total);

  const maxTotal     = allStats[0]?.stats.total || 1;
  const topByUnits   = [...allStats].sort((a, b) => b.stats.count - a.stats.count)[0];
  const globalTopUnit = allStats
    .map(c => c.stats.topUnit ? { ...c.stats.topUnit, _customerName: c.name } : null)
    .filter(Boolean)
    .sort((a, b) => (b.priceJPY || 0) - (a.priceJPY || 0))[0];

  // ── Select customer helper ───────────────────────────────────────────────
  const selectCustomer = (id) => {
    setSelected(id);
    setSearch('');
    setDetailUnit(null);
  };

  // ── Profile View ─────────────────────────────────────────────────────────
  if (selected) {
    const customer = customers.find(c => c.id === selected);
    if (!customer) { setSelected(null); return null; }
    const stats    = getCustomerStats(selected);
    const total    = stats.count || 1;
    const vPct     = Math.round((stats.vehicles.length / total) * 100);
    const ePct     = Math.round((stats.engines.length  / total) * 100);
    const pPct     = 100 - vPct - ePct;

    const openUnit = (item) =>
      setDetailUnit({ item, type: TYPE_MAP[item._type] });

    const safe = (v) => v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v);
    const infoRows = [
      [t('country'),  safe(customer.country)],
      ['Currency',          safe(customer.currency)],
      [t('email'),    safe(customer.email)],
      [t('phone'),    safe(customer.phone) || '—'],
      ['Joined',            customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : '—'],
    ];

    const loginSessions = (Array.isArray(customer.loginSessions) ? customer.loginSessions : []).slice().reverse();

    return (
      <div>
        {/* Header row: back + admin actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setSelected(null)}>
            ← Back
          </button>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* Download customer stock — PDF or Excel */}
            {stats.count > 0 && (
              <div style={{ position: 'relative' }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setDlOpen(o => !o)}
                >
                  ⬇ Download ▾
                </button>
                {dlOpen && (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 6px)', left: 0,
                    background: 'var(--bg2)', border: '1px solid var(--border)',
                    borderRadius: 10, padding: 6, zIndex: 200, minWidth: 170,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  }}>
                    <button
                      className="dl-option-btn"
                      onClick={() => {
                        exportCSV(stats.allItems, `${(customer.name || 'customer').replace(/\s+/g,'-')}-stock-${new Date().toISOString().slice(0,10)}.csv`);
                        setDlOpen(false);
                      }}
                    >
                      📊 Excel / CSV
                    </button>
                    <button
                      className="dl-option-btn"
                      onClick={() => {
                        exportPDF(stats.allItems, customer.name);
                        setDlOpen(false);
                      }}
                    >
                      📄 PDF (simple)
                    </button>
                    <button
                      className="dl-option-btn"
                      onClick={() => {
                        generateCustomerStockPDF(customer.id);
                        setDlOpen(false);
                      }}
                    >
                      📷 Stock PDF (with images)
                    </button>
                  </div>
                )}
              </div>
            )}
            {role.canDelete && (
              <>
                <button className="btn btn-sm btn-secondary" onClick={() => setEditCustomer({ ...customer })}>
                  <EditIcon /> Edit
                </button>
                <button
                  className="btn btn-sm"
                  style={customer.blocked
                    ? { background: 'rgba(63,185,80,0.15)', color: 'var(--green)', border: '1px solid rgba(63,185,80,0.4)' }
                    : { background: 'rgba(248,81,73,0.1)', color: 'var(--red)', border: '1px solid rgba(248,81,73,0.3)' }}
                  onClick={() => handleBlock(customer.id, customer.blocked)}
                >
                  {customer.blocked ? '✓ Unblock Account' : '⊘ Block Account'}
                </button>
                <button
                  className="btn btn-sm"
                  style={{ background: 'var(--red)', color: '#fff', border: 'none' }}
                  onClick={() => handleDelete(customer.id)}
                >
                  Delete Customer
                </button>
              </>
            )}
          </div>
        </div>

        <div className="customer-profile-grid">

          {/* ── Left Column: Info + Payment Terms ── */}
          <div>
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center' }}>
              <div className="customer-avatar" style={{ width: 70, height: 70, fontSize: 28, margin: '0 auto 16px' }}>
                {(customer.name || '?').charAt(0)}
              </div>
              <h2 style={{ marginBottom: 4 }}>{customer.name || 'Unknown'}</h2>
              {customer.blocked && (
                <div style={{ marginBottom: 8 }}>
                  <span className="blocked-badge">⊘ Account Blocked</span>
                </div>
              )}
              <div style={{ color: 'var(--text2)', marginBottom: 16 }}>{customer.company}</div>

              {/* Walk-in upgrade banner */}
              {customer.is_walk_in && role.canDelete && (
                <div style={{ padding: '12px 14px', background: 'rgba(210,153,34,0.08)', border: '1px solid rgba(210,153,34,0.2)', borderRadius: 8, marginBottom: 16, textAlign: 'left', fontSize: 12 }}>
                  <div style={{ fontWeight: 700, color: 'var(--yellow)', marginBottom: 4 }}>Walk-in Customer</div>
                  <div style={{ color: 'var(--text3)', marginBottom: 8 }}>This customer only has basic details. Upgrade to give them full portal access.</div>
                  <button className="btn btn-primary btn-sm" onClick={() => {
                    const pw = Math.random().toString(36).slice(2, 6) + Math.random().toString(36).slice(2, 6).toUpperCase();
                    const email = prompt('Enter email for login (required):');
                    if (!email || !email.trim()) return;
                    const all = DB.get('customers') || [];
                    DB.set('customers', all.map(c => c.id === customer.id ? { ...c, is_walk_in: false, email: email.trim(), password: pw } : c));
                    alert(`Upgraded! Login credentials:\nEmail: ${email.trim()}\nPassword: ${pw}\n\nShare these with the customer.`);
                    forceUpdate(v => v + 1);
                  }}>Upgrade to Portal Customer</button>
                </div>
              )}

              <div style={{ textAlign: 'left', fontSize: 13 }}>
                {infoRows.map(([label, value], i) => (
                  <div key={label} style={{
                    padding: '10px 0',
                    borderBottom: i < infoRows.length - 1 ? '1px solid var(--border)' : 'none',
                    display: 'flex', justifyContent: 'space-between', gap: 8,
                  }}>
                    <span style={{ color: 'var(--text3)', flexShrink: 0 }}>{label}</span>
                    <span style={{ textAlign: 'right', wordBreak: 'break-all' }}>{value}</span>
                  </div>
                ))}
              </div>

              {/* Login Sessions */}
              {loginSessions.length > 0 && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)', textAlign: 'left' }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
                    Login History ({loginSessions.length})
                  </div>
                  <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {loginSessions.map((s, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '6px 8px', background: 'var(--bg3)', borderRadius: 6 }}>
                        <span style={{ color: 'var(--text2)' }}>
                          {new Date(s.time).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span style={{ color: s.device === 'Mobile' ? 'var(--yellow)' : 'var(--blue)', fontWeight: 600, fontSize: 10 }}>
                          {s.device}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Payment Terms & Documents Card ── */}
          <PaymentTermsCard customer={customer} onSave={() => forceUpdate(v => v + 1)} />
          </div>

          {/* ── Right Column ── */}
          <div>
            {/* Stat row */}
            <div className="stats-grid cust-profile-stats" style={{ marginBottom: 20 }}>
              <StatCard icon={PackageIcon} value={stats.stockItems.length}    label="Stock"     color="blue"   onClick={() => setProfileTab('stock')} />
              <StatCard icon={CarIcon}     value={stats.selectedItems.length} label="Selected"  color="yellow" onClick={() => setProfileTab('selected')} />
              <StatCard icon={PartsIcon}   value={stats.assignedItems.length} label="Assigned"  color="green"  onClick={() => setProfileTab('assigned')} />
              <StatCard icon={EngineIcon}  value={`$${(stats.outstanding || 0).toLocaleString()}`} label="Balance" color={stats.outstanding > 0 ? 'red' : 'green'} />
            </div>

            {/* Highest value unit */}
            {stats.topUnit && (
              <div
                className="top-unit-card"
                style={{ cursor: 'pointer' }}
                onClick={() => openUnit(stats.topUnit)}
                title="Click to view unit"
              >
                <div className="top-unit-badge">💎 Highest Value Unit — click to view</div>
                <div className="top-unit-name">
                  {stats.topUnit.make
                    ? `${stats.topUnit.make} ${stats.topUnit.model}`
                    : stats.topUnit.engineName || stats.topUnit.partName}
                </div>
                <div className="top-unit-sku">{stats.topUnit.sku}</div>
                <div className="top-unit-price">{formatPrice(stats.topUnit.priceJPY, currency)}</div>
              </div>
            )}

            {/* Composition bar */}
            {stats.count > 0 && (
              <div className="card" style={{ padding: 16, marginBottom: 16 }}>
                <div className="comp-label">
                  <span>Unit Composition</span>
                  <span>{stats.count} total</span>
                </div>
                <div className="comp-bar">
                  {vPct > 0 && <div className="comp-bar-segment" style={{ width: `${vPct}%`, background: 'var(--blue)'   }} />}
                  {ePct > 0 && <div className="comp-bar-segment" style={{ width: `${ePct}%`, background: 'var(--green)'  }} />}
                  {pPct > 0 && <div className="comp-bar-segment" style={{ width: `${pPct}%`, background: 'var(--orange)' }} />}
                </div>
                <div className="comp-legend">
                  {stats.vehicles.length > 0 && <div className="comp-legend-item"><div className="comp-legend-dot" style={{ background: 'var(--blue)'   }} />{stats.vehicles.length} {t('vehicles')}</div>}
                  {stats.engines.length  > 0 && <div className="comp-legend-item"><div className="comp-legend-dot" style={{ background: 'var(--green)'  }} />{stats.engines.length}  {t('engines')}</div>}
                  {stats.parts.length    > 0 && <div className="comp-legend-item"><div className="comp-legend-dot" style={{ background: 'var(--orange)' }} />{stats.parts.length}    {t('parts')}</div>}
                </div>
              </div>
            )}

            {/* ── Tabs: Stock / Selected / Assigned ── */}
            <div className="tabs" style={{ marginBottom: 14 }}>
              <button className={`tab ${profileTab === 'stock' ? 'active' : ''}`} onClick={() => setProfileTab('stock')}>
                Stock <span className="cnt">{stats.stockItems.length}</span>
              </button>
              <button className={`tab ${profileTab === 'selected' ? 'active' : ''}`} onClick={() => setProfileTab('selected')}>
                Selected <span className="cnt">{stats.selectedItems.length}</span>
              </button>
              <button className={`tab ${profileTab === 'assigned' ? 'active' : ''}`} onClick={() => setProfileTab('assigned')}>
                Assigned <span className="cnt">{stats.assignedItems.length}</span>
              </button>
            </div>

            <div className="card">
              <div className="card-header">
                <div className="card-title">
                  <StockIcon />
                  {profileTab === 'stock' ? 'Stock (Wishlist)' : profileTab === 'selected' ? 'Selected (Pending Approval)' : 'Assigned (Sold)'}
                </div>
                {profileTab === 'stock' && stats.stockItems.length > 0 && (
                  <button className="btn btn-secondary btn-sm" onClick={() => generateCustomerStockPDF(selected)}>📄 PDF</button>
                )}
              </div>
              <div className="card-body">
                {/* Toolbar */}
                {(() => {
                  const tabItems = profileTab === 'stock' ? stats.stockItems : profileTab === 'selected' ? stats.selectedItems : stats.assignedItems;
                  const tabFiltered = tabItems.filter(item => {
                    if (!search) return true;
                    const name = item.make ? `${item.make} ${item.model}` : item.engineName || item.partName || '';
                    return name.toLowerCase().includes(search.toLowerCase()) || (item.sku || '').toLowerCase().includes(search.toLowerCase());
                  });

                  if (tabItems.length === 0) {
                    return (
                      <div style={{ textAlign: 'center', color: 'var(--text3)', padding: 30 }}>
                        {profileTab === 'stock' ? 'No units in stock' : profileTab === 'selected' ? 'No pending selections' : 'No assigned units'}
                      </div>
                    );
                  }

                  return (
                    <>
                      <div className="units-toolbar">
                        <input className="units-search" placeholder="Search by name or SKU…" value={search} onChange={e => setSearch(e.target.value)} />
                        <span className="units-count">{tabFiltered.length} / {tabItems.length}</span>
                      </div>

                      {tabFiltered.length === 0 ? (
                        <div style={{ textAlign: 'center', color: 'var(--text3)', padding: 30 }}>No units match your search</div>
                      ) : (
                        tabFiltered.map(item => {
                          const name = item.make ? `${item.make} ${item.model}` : item.engineName || item.partName || 'Unit';
                          const containers = DB.get('containers') || [];
                          const cont = containers.find(c => (c.units || []).includes(item.id));
                          return (
                            <button key={item.id} className="stock-item-btn" onClick={() => openUnit(item)}>
                              <div className="stock-img">
                                {item.images?.length
                                  ? <img src={item.images[0]} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  : item._type === 'vehicle' ? <CarIcon /> : item._type === 'engine' ? <EngineIcon /> : <PartsIcon />}
                              </div>
                              <div className="stock-info">
                                <div className="stock-title">{name}</div>
                                <div className="stock-sku">{item.sku}{item.chassis ? ` · ${item.chassis}` : ''}</div>
                                <div className="stock-meta">
                                  {item.year || item.condition || item.category}
                                  {item.grade ? ` · Grade ${item.grade}` : ''}
                                  {item.mileage ? ` · ${Number(item.mileage).toLocaleString()} km` : ''}
                                  {profileTab === 'assigned' && cont ? ` · 🚢 ${cont.number || 'Container'}` : ''}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <span className={`status status-${item.status}`} style={{ fontSize: 9 }}>{(item.status || '').replace(/_/g, ' ')}</span>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* ── FOB Quote Requests from this customer ── */}
        {(() => {
          const allRequests = DB.get('requests') || [];
          const custRequests = allRequests.filter(r => r.customerId === selected);
          const fobRequests = custRequests.filter(r => r.type === 'FOB Quote' || r.unitIds?.length > 0);
          if (fobRequests.length === 0) return null;

          const allUnitsLookup = [...vehicles, ...engines, ...parts];
          const getUnitName = (id) => {
            const u = allUnitsLookup.find(x => x.id === id);
            return u ? (u.make ? `${u.make} ${u.model}` : u.engineName || u.partName) : id;
          };

          return (
            <div className="card" style={{ marginTop: 20 }}>
              <div className="card-header">
                <div className="card-title"><RequestIcon />FOB Quote Requests</div>
              </div>
              <div className="card-body">
                {fobRequests.map(req => (
                  <div key={req.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>{req.createdAt} · #{req.id}</span>
                      <span className={`status status-${req.status === 'pending' ? 'pending' : req.status === 'processing' ? 'transit' : req.status === 'sourced' ? 'available' : 'assigned'}`} style={{ fontSize: 10 }}>
                        {req.status}
                      </span>
                    </div>
                    {req.unitIds?.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {req.unitIds.map(uid => (
                          <div key={uid} style={{ fontSize: 12, color: 'var(--text2)', padding: '4px 8px', background: 'var(--bg3)', borderRadius: 6 }}>
                            {getUnitName(uid)}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'pre-line' }}>{req.details}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Unit Detail Modal */}
        {detailUnit && (
          <DetailModal
            item={detailUnit.item}
            type={detailUnit.type}
            onClose={() => setDetailUnit(null)}
            currency={currency}
            canPrice={canPrice}
            isCustomer={isCustomer}
            lang={lang}
            onUpdate={(updated) => handleUnitUpdate(updated, detailUnit.type)}
          />
        )}

        {/* Edit Customer Modal */}
        {editCustomer && (
          <div className="modal-overlay" onClick={() => setEditCustomer(null)}>
            <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">Edit Customer</h3>
                <button className="modal-close" onClick={() => setEditCustomer(null)}>×</button>
              </div>
              <div className="modal-body" style={{ padding: 20 }}>
                {[
                  { key: 'name', label: 'Name', type: 'text' },
                  { key: 'email', label: 'Email / Username', type: 'email' },
                  { key: 'phone', label: 'Phone', type: 'tel' },
                  { key: 'company', label: 'Company', type: 'text' },
                  { key: 'country', label: 'Country', type: 'text' },
                  { key: 'city', label: 'City', type: 'text' },
                  { key: 'currency', label: 'Currency', type: 'select', options: Object.keys(CURRENCIES) },
                  { key: 'tradeLicense', label: 'Trade License', type: 'text' },
                  { key: 'customerType', label: 'Type', type: 'select', options: ['individual', 'business'] },
                ].map(f => (
                  <div key={f.key} style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 4, textTransform: 'uppercase' }}>{f.label}</label>
                    {f.type === 'select' ? (
                      <select className="form-input" value={editCustomer[f.key] || ''} onChange={e => setEditCustomer(c => ({ ...c, [f.key]: e.target.value }))}>
                        <option value="">—</option>
                        {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input className="form-input" type={f.type} value={editCustomer[f.key] || ''} onChange={e => setEditCustomer(c => ({ ...c, [f.key]: e.target.value }))} />
                    )}
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setEditCustomer(null)}>Cancel</button>
                  <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => {
                    const all = DB.get('customers') || [];
                    DB.setAsync('customers', all.map(c => c.id === editCustomer.id ? { ...c, ...editCustomer } : c));
                    setEditCustomer(null);
                    setSelected(editCustomer.id);
                    forceUpdate(v => v + 1);
                  }}>Save Changes</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── List View ─────────────────────────────────────────────────────────────
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h2 style={{ margin: 0 }}>{t('customers')}</h2>
        {role.canAdd && <button className="btn btn-primary" onClick={() => setAddModal(true)}><PlusIcon />Add Customer</button>}
      </div>

      {/* Category tabs */}
      <div className="tabs" style={{ marginBottom: 14 }}>
        {[
          { id: 'all', label: 'All', count: customers.length },
          { id: 'business', label: 'Business / Dealers', count: customers.filter(c => c.customerType === 'business' && !c.is_walk_in).length },
          { id: 'individual', label: 'Individual', count: customers.filter(c => (c.customerType === 'individual' || !c.customerType) && !c.is_walk_in).length },
          { id: 'walkin', label: 'Yard Walk-ins', count: customers.filter(c => c.is_walk_in).length },
        ].map(tab => (
          <button key={tab.id} className={`tab ${custTab === tab.id ? 'active' : ''}`} onClick={() => { setCustTab(tab.id); setListSearch(''); }}>
            {tab.label} <span className="cnt">{tab.count}</span>
          </button>
        ))}
      </div>

      <input
        className="units-search"
        style={{ marginBottom: 20, width: '100%', boxSizing: 'border-box' }}
        placeholder="Search by name, company, country or email…"
        value={listSearch}
        onChange={e => setListSearch(e.target.value)}
      />

      {/* Analytics */}
      {allStats.length > 0 && (
        <div className="metric-section">
          <div className="metric-title"><ActivityIcon />Customer Portfolio Analysis</div>

          {allStats.map((c, i) => (
            <div key={c.id} className="metric-bar-row">
              <div className="metric-rank">
                {i < 3
                  ? RANK_LABELS[i]
                  : <span style={{ color: 'var(--text3)', fontSize: 12 }}>{i + 1}</span>}
              </div>
              <div className="metric-bar-label" title={c.name}>{c.name.split(' ')[0]}</div>
              <div className="metric-bar-track">
                <div
                  className="metric-bar-fill"
                  style={{
                    width: `${maxTotal > 0 ? (c.stats.total / maxTotal) * 100 : 0}%`,
                    background: BAR_COLORS[i % BAR_COLORS.length],
                  }}
                />
              </div>
              <div className="metric-bar-value">
                {formatPrice(c.stats.total, currency)}
                <span style={{ color: 'var(--text3)', marginLeft: 6 }}>· {c.stats.count}u</span>
              </div>
            </div>
          ))}

          <div className="metric-insights">
            <div className="metric-insight">
              <div className="metric-insight-icon">🏆</div>
              <div className="metric-insight-label">Best Customer</div>
              <div className="metric-insight-name">{allStats[0]?.name || '—'}</div>
              <div className="metric-insight-sub">{allStats[0]?.company}</div>
              <div className="metric-insight-value">{formatPrice(allStats[0]?.stats.total || 0, currency)}</div>
            </div>
            <div className="metric-insight">
              <div className="metric-insight-icon">💎</div>
              <div className="metric-insight-label">Highest Value Unit</div>
              <div className="metric-insight-name">
                {globalTopUnit
                  ? (globalTopUnit.make ? `${globalTopUnit.make} ${globalTopUnit.model}` : globalTopUnit.engineName || globalTopUnit.partName)
                  : '—'}
              </div>
              <div className="metric-insight-sub">{globalTopUnit?._customerName}</div>
              <div className="metric-insight-value">
                {globalTopUnit ? formatPrice(globalTopUnit.priceJPY, currency) : '—'}
              </div>
            </div>
            <div className="metric-insight">
              <div className="metric-insight-icon">📦</div>
              <div className="metric-insight-label">Most Units Ordered</div>
              <div className="metric-insight-name">{topByUnits?.name || '—'}</div>
              <div className="metric-insight-sub">{topByUnits?.company}</div>
              <div className="metric-insight-value">{topByUnits?.stats.count || 0} units</div>
            </div>
          </div>
        </div>
      )}

      {/* Customer cards — sorted by portfolio value */}
      {allStats.filter(c => {
        // Tab filter
        if (custTab === 'business' && (c.customerType !== 'business' || c.is_walk_in)) return false;
        if (custTab === 'individual' && (c.customerType === 'business' || c.is_walk_in)) return false;
        if (custTab === 'walkin' && !c.is_walk_in) return false;
        // Search filter
        if (!listSearch.trim()) return true;
        const q = listSearch.toLowerCase();
        return (
          c.name?.toLowerCase().includes(q) ||
          c.company?.toLowerCase().includes(q) ||
          c.country?.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q)
        );
      }).map(customer => {
        const { stats } = customer;
        return (
          <div
            key={customer.id}
            className={`customer-card ${customer.blocked ? 'is-blocked' : ''}`}
            onClick={() => selectCustomer(customer.id)}
          >
            <div className="customer-avatar">{(customer.name || '?').charAt(0)}</div>
            <div className="customer-info">
              <div className="customer-name" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {customer.name}
                {customer.blocked && <span className="blocked-badge">Blocked</span>}
              </div>
              <div className="customer-company">{customer.company} · {customer.country}</div>
              <div className="customer-stats">
                <span><strong>{stats.vehicles.length}</strong> vehicles</span>
                <span><strong>{stats.engines.length}</strong> engines</span>
                <span><strong>{stats.parts.length}</strong> parts</span>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="customer-units" style={{ fontSize: 13, fontWeight: 700 }}>{stats.count} units</div>
            </div>
          </div>
        );
      })}

      {addModal && (
        <AddCustomerModal
          onSave={() => { setAddModal(false); forceUpdate(v => v + 1); }}
          onClose={() => setAddModal(false)}
        />
      )}

    </div>
  );
}

// Extracted as proper component so React hooks work correctly
function PaymentTermsCard({ customer, onSave }) {
  const safeParse = (val) => {
    if (!val) return null;
    if (typeof val === 'string') { try { return JSON.parse(val); } catch { return null; } }
    return val;
  };

  const rawTerms = safeParse(customer.paymentTerms) || safeParse(customer.payment_terms) || { milestones: [{ id: 'first_payment', label: 'Before shipping', percentage: 50 }, { id: 'final_payment', label: 'Before BL surrender', percentage: 50 }] };
  const initMilestones = (Array.isArray(rawTerms?.milestones) ? rawTerms.milestones : []).filter(m => m && typeof m === 'object');
  const initDeposit = customer.depositRequired === true || customer.deposit_required === true;
  const initDepPct = Number(customer.depositPercentage || customer.deposit_percentage) || 0;

  let initReqDocs = safeParse(customer.requiredDocuments) || safeParse(customer.required_documents);
  if (!Array.isArray(initReqDocs)) initReqDocs = ['trade_license', 'contract'];
  let initUpDocs = safeParse(customer.uploadedDocuments) || safeParse(customer.uploaded_documents);
  if (!Array.isArray(initUpDocs)) initUpDocs = [];

  const DOC_OPTIONS = [
    { id: 'trade_license', label: 'Trade License' }, { id: 'contract', label: 'Contract' },
    { id: 'id_passport', label: 'ID / Passport' }, { id: 'bank_details', label: 'Bank Details' },
    { id: 'import_license', label: 'Import License' }, { id: 'other', label: 'Other' },
  ];

  const [editTerms, setEditTerms] = useState(false);
  const [ms, setMs] = useState(initMilestones);
  const [dep, setDep] = useState(initDeposit);
  const [depPct, setDepPct] = useState(initDepPct);
  const [docs, setDocs] = useState(initReqDocs);
  const totalPct = ms.reduce((s, m) => s + (Number(m.percentage) || 0), 0);

  const saveTerms = () => {
    const updated = { ...customer, paymentTerms: { milestones: ms }, depositRequired: dep, depositPercentage: Number(depPct) || 0, requiredDocuments: docs };
    DB.set('customers', (DB.get('customers') || []).map(c => c.id === customer.id ? updated : c));
    setEditTerms(false);
    onSave?.();
  };

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-header" style={{ padding: '12px 16px' }}>
        <div className="card-title" style={{ fontSize: 13 }}>Payment Terms</div>
        {!editTerms && <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => setEditTerms(true)}>Edit</button>}
      </div>
      <div className="card-body" style={{ padding: '12px 16px' }}>
        {!editTerms ? (
          <>
            <div style={{ marginBottom: 12 }}>
              {initMilestones.length > 0 ? initMilestones.map((m, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: i < initMilestones.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 12 }}>
                  <span>{String(m.label || '')}</span>
                  <strong>{String(m.percentage || 0)}%</strong>
                </div>
              )) : <div style={{ fontSize: 12, color: 'var(--text3)' }}>No milestones set</div>}
            </div>
            {initDeposit && <div style={{ fontSize: 12, color: 'var(--blue)' }}>Deposit required: {initDepPct}%</div>}
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Required Documents</div>
              {DOC_OPTIONS.filter(d => initReqDocs.includes(d.id)).map(d => {
                const uploaded = initUpDocs.some(u => u && u.type === d.id);
                return <div key={d.id} style={{ fontSize: 11, padding: '3px 0', color: uploaded ? 'var(--green)' : 'var(--red)' }}>{uploaded ? '✅' : '❌'} {d.label}</div>;
              })}
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 6 }}>Milestones</div>
              {ms.map((m, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                  <input className="form-input" value={m.label || ''} onChange={e => setMs(prev => prev.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} style={{ flex: 1, fontSize: 12, padding: '5px 8px' }} placeholder="Label" />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                    <input type="number" className="form-input" value={m.percentage || 0} min={0} max={100} onChange={e => setMs(prev => prev.map((x, j) => j === i ? { ...x, percentage: Number(e.target.value) || 0 } : x))} style={{ width: 60, fontSize: 12, padding: '5px 6px', textAlign: 'right' }} />
                    <span style={{ fontSize: 11, color: 'var(--text3)' }}>%</span>
                  </div>
                  {ms.length > 1 && <button onClick={() => setMs(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 14, padding: 2 }}>×</button>}
                </div>
              ))}
              <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, marginBottom: 6 }} onClick={() => setMs(prev => [...prev, { id: `ms_${Date.now()}`, label: '', percentage: 0 }])}>+ Add Milestone</button>
              {totalPct !== 100 && <div style={{ fontSize: 11, color: 'var(--red)', fontWeight: 600 }}>Total is {totalPct}% — must be 100%</div>}
              {totalPct === 100 && <div style={{ fontSize: 11, color: 'var(--green)' }}>100%</div>}
            </div>
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" checked={dep} onChange={e => setDep(e.target.checked)} style={{ accentColor: 'var(--blue)' }} />
              <span style={{ fontSize: 12 }}>Deposit required</span>
              {dep && <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}><input type="number" className="form-input" value={depPct} onChange={e => setDepPct(e.target.value)} style={{ width: 60, fontSize: 12, padding: '4px 6px', textAlign: 'right' }} /><span style={{ fontSize: 11, color: 'var(--text3)' }}>%</span></div>}
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 6 }}>Required Documents</div>
              {DOC_OPTIONS.map(d => (
                <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', marginBottom: 4 }}>
                  <input type="checkbox" checked={docs.includes(d.id)} onChange={e => setDocs(prev => e.target.checked ? [...prev, d.id] : prev.filter(x => x !== d.id))} style={{ accentColor: 'var(--blue)' }} />
                  {d.label}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditTerms(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={totalPct !== 100} onClick={saveTerms}>Save Terms</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Customers;
